import { Injectable } from '@angular/core';
import { fromJS, List } from 'immutable';
import { has, isBoolean, isNumber, keys, reduce } from 'lodash';
import {
  IFetchFilterKey,
  ISearchParams,
  SEARCH_LIST_FILTER,
  SearchParamsEnum,
  SortByEnum,
  SortOrderEnum,
} from '../entity-data/entity-data.data';
import {
  getInContent,
  IS_DIRTY_KEY,
  IS_NEW_KEY,
  setInContent,
} from '../entity-data/entity.model';
import { Settings } from '../settings-data/settings.model';
import { DateTimeService } from 'i2g-ng-utils';
import { TrackedTime } from './models/tracked-time.model';
import {
  ALL_YEARS,
  BILLED_SEARCH_PARAMS_KEY,
  TrackedTimeFiltersEnum,
  UNBILLED_SEARCH_PARAMS_KEY,
} from './tracked-time-data.data';
import { EntityDataBuilder } from '../entity-data/entity-data.builder';
import { UrlService } from '../shared/services/url/url.service';
import { EntityTypesEnum } from '../../app.enum';

@Injectable()
export class TrackedTimeDataBuilder extends EntityDataBuilder<TrackedTime> {
  model = TrackedTime;
  entityType: EntityTypesEnum = EntityTypesEnum.tracked_time;
  yearFilterKey: string = SearchParamsEnum.year;
  listFilterKeys: IFetchFilterKey = {
    [TrackedTimeFiltersEnum.unbilled]: UNBILLED_SEARCH_PARAMS_KEY,
    [TrackedTimeFiltersEnum.billed]: BILLED_SEARCH_PARAMS_KEY,
  };
  refreshFiltersDelay = 0;

  constructor(
    private _dateTimeService: DateTimeService,
    urlService: UrlService
  ) {
    super(urlService);
  }

  buildEntity(
    props: any = {},
    settings: Settings,
    isDirty?: boolean,
    isNew?: boolean
  ): TrackedTime {
    const localeKey = TrackedTime.CONTENT_KEYS.LOCALE;

    props = props && props.toMap ? props.toMap() : fromJS(props || {});

    const trackedTimeProps = props.withMutations((map) => {
      // Default locale
      if (!getInContent(map, localeKey)) {
        setInContent(map, localeKey, settings.documentLocale);
      }

      if (isBoolean(isDirty)) {
        map.set(IS_DIRTY_KEY, isDirty);
      }

      if (isBoolean(isNew)) {
        map.set(IS_NEW_KEY, isNew);
      }
    });

    return new TrackedTime(trackedTimeProps);
  }

  buildEntities(array) {
    return List<TrackedTime>(
      array ? array.map(this.buildEntity.bind(this)) : []
    );
  }

  buildSortItemsComparatorFn(
    sortBy: SortByEnum,
    sortOrderEnum: SortOrderEnum
  ): (itemA: TrackedTime, itemB: TrackedTime) => number {
    const MAX_DEPTH = 1; // prevent infinite recursion
    const sortOrder = sortOrderEnum === SortOrderEnum.asc ? 1 : -1;
    const dateSort = (order: number, depth: number): any => {
      return (trackedTimeA: TrackedTime, trackedTimeB: TrackedTime): number => {
        const trackedTimeDateA = this._dateTimeService.getMilliseconds(
          this._dateTimeService.formatDate(trackedTimeA.startDate)
        );
        const trackedTimeDateB = this._dateTimeService.getMilliseconds(
          this._dateTimeService.formatDate(trackedTimeB.startDate)
        );

        if (trackedTimeDateA > trackedTimeDateB) {
          return order;
        } else if (trackedTimeDateA < trackedTimeDateB) {
          return order * -1;
        } else {
          return depth < MAX_DEPTH
            ? billingNameSort(1, depth + 1)(trackedTimeA, trackedTimeB)
            : 0;
        }
      };
    };
    const billingNameSort = (order: number, depth: number): any => {
      return (trackedTimeA: TrackedTime, trackedTimeB: TrackedTime) => {
        const billingNameA = (trackedTimeA.billingName || '').toLowerCase();
        const billingNameB = (trackedTimeB.billingName || '').toLowerCase();
        if (billingNameA > billingNameB) {
          return order;
        } else if (billingNameA < billingNameB) {
          return order * -1;
        } else {
          return depth < MAX_DEPTH
            ? dateSort(-1, depth + 1)(trackedTimeA, trackedTimeB)
            : 0;
        }
      };
    };

    switch (sortBy) {
      case SortByEnum.client_name:
        return billingNameSort(sortOrder, 0);
      case SortByEnum.start_date:
        return dateSort(sortOrder, 0);
      default:
        return null;
    }
  }

  buildListItemsFilterFn(
    searchParams: ISearchParams,
    listFilter: string
  ): (entity: TrackedTime) => boolean {
    const year = searchParams[this.yearFilterKey];
    const billedUnbilledFilter =
      listFilter === TrackedTimeFiltersEnum.billed
        ? (trackedTime: TrackedTime) => trackedTime.billedDocument
        : (trackedTime: TrackedTime) => !trackedTime.billedDocument;
    const yearFilter =
      year && year !== ALL_YEARS
        ? (trackedTime: TrackedTime) =>
            this._dateTimeService.parse(trackedTime.startDate).year === year
        : (trackedTime: TrackedTime) => true;

    return listFilter !== SEARCH_LIST_FILTER
      ? (trackedTime: TrackedTime) =>
          billedUnbilledFilter(trackedTime) && yearFilter(trackedTime)
      : (trackedTime: TrackedTime) => yearFilter(trackedTime);
  }

  buildListFilter(entity: any): string {
    const trackedTime: TrackedTime = entity as TrackedTime;

    return trackedTime.billedDocument
      ? TrackedTimeFiltersEnum.billed
      : TrackedTimeFiltersEnum.unbilled;
  }

  validateSearchParams(searchParams: ISearchParams, filter: string) {
    const validParams = {
      [SearchParamsEnum.sort_by]: (a) =>
        [SortByEnum.client_name, SortByEnum.start_date].includes(a),
      [SearchParamsEnum.sort_order]: (a) =>
        [SortOrderEnum.asc, SortOrderEnum.desc].includes(a),
      [SearchParamsEnum.year]: isNumber,
    };
    const requiredKeys = keys(validParams).every((key) =>
      has(searchParams, key)
    );
    const validValues = reduce(
      searchParams,
      (result, value, key) =>
        result && has(validParams, key) && validParams[key](value),
      true
    );

    return requiredKeys && validValues;
  }
}
